import {
  FocusEventHandler,
  forwardRef,
  ReactNode,
  Ref,
  useEffect,
  useState,
} from 'react';

import InsertInvitationIcon from '@mui/icons-material/InsertInvitation';
import { FormHelperText, InputLabel } from '@mui/material';
import {
  DateTimePickerProps,
  LocalizationProvider,
  DateTimePicker as MuiDatetimePicker,
} from '@mui/x-date-pickers-pro';
import { AdapterMoment } from '@mui/x-date-pickers-pro/AdapterMoment';
import moment, { Moment } from 'moment';

import { ActionBar } from '@/components';

import { DateTimePickerRoot, componentCustomStyles } from './styles';
import { BaseFieldProps } from '../types';

export interface BaseDateTimePickerProps
  extends DateTimePickerProps<Moment | string>,
    BaseFieldProps {
  hideInput?: boolean;
  pickerIcon?: ReactNode | null;
  clearable?: boolean;
  onClear?: () => void;
  onAccept?: (value: Moment | string | null) => void;
  onKeyUp?: (
    event: React.KeyboardEvent<HTMLDivElement | HTMLInputElement>,
  ) => void;
  onBlur?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  allowFreeInput?: boolean;
}

const DEFAULT_MIN_DATE = moment('2000-01-01');

export const DateTimePicker = forwardRef<Ref<any>, BaseDateTimePickerProps>(
  (props, ref) => {
    const [open, setOpen] = useState(false);
    const [internalValue, setInternalValue] = useState<Moment | null>(null);
    const [tempValue, setTempValue] = useState<Moment | null>(null);

    const {
      label,
      error,
      hideInput,
      pickerIcon,
      clearable,
      onKeyUp,
      onClear,
      onAccept,
      onBlur,
      allowFreeInput = true,
      value,
      onChange,
      minDate,
      maxDate,
      ...rest
    } = props;

    const effectiveMinDate = (minDate as Moment) ?? DEFAULT_MIN_DATE;
    const effectiveMaxDate = props.disableFuture
      ? moment()
      : (maxDate as Moment) ?? moment().add(1, 'year').endOf('year');

    useEffect(() => {
      if (value) {
        const momentValue = moment(value);
        setInternalValue(momentValue);
        setTempValue(momentValue);
      } else {
        setInternalValue(null);
        setTempValue(null);
      }
    }, [value]);

    const isInRange = (v: Moment | null): boolean => {
      if (!v || !v.isValid()) return false;
      const max = props.disableFuture ? moment() : effectiveMaxDate;
      return (
        v.isSameOrAfter(effectiveMinDate) && v.isSameOrBefore(max, 'minute')
      );
    };

    const rangeError =
      tempValue?.isValid() && !isInRange(tempValue)
        ? `Year must be between ${effectiveMinDate.year()} and ${effectiveMaxDate.year()}`
        : undefined;

    const commitValue = (nextValue: Moment | null) => {
      if (nextValue && !isInRange(nextValue)) return;
      setInternalValue(nextValue);
      setTempValue(nextValue);
      onChange?.(nextValue, { validationError: null });
      if (onAccept) {
        onAccept(nextValue);
      }
    };

    const handleKeyDown = (
      event: React.KeyboardEvent<HTMLDivElement | HTMLInputElement>,
    ) => {
      if (allowFreeInput) {
        if (event.key === 'Enter' && tempValue?.isValid()) {
          event.preventDefault();
          commitValue(tempValue);
          setOpen(false);
        }
        onKeyUp?.(event);
        return;
      }

      if (event.key !== 'Tab') {
        event.preventDefault();
      }
      if (event.key === 'Enter') {
        const now = moment();
        commitValue(now);
      }
      onKeyUp?.(event);
    };

    // Default icon with toggle behavior
    const defaultIcon = (
      <InsertInvitationIcon
        onClick={() => setOpen(!open)}
        sx={{
          padding: 0.5,
          '&:hover': {
            borderRadius: '100%',
            backgroundColor: '#EDEDED',
            boxShadow: '0 0 0 2px #EDEDED',
          },
        }}
      />
    );

    // Wrap the custom icon in a container to preserve click behavior
    const renderedPickerIcon = pickerIcon ? (
      <div onClick={() => setOpen(!open)} style={{ cursor: 'pointer' }}>
        {pickerIcon}
      </div>
    ) : (
      defaultIcon
    );

    return (
      <DateTimePickerRoot className={hideInput ? 'hide-input' : ''}>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          {label && <InputLabel>{label}</InputLabel>}
          <MuiDatetimePicker
            ampm={false}
            timeSteps={{ minutes: 1 }}
            minDate={effectiveMinDate}
            maxDate={effectiveMaxDate}
            {...rest}
            value={internalValue}
            onChange={(newValue) => {
              const momentValue = newValue ? moment(newValue) : null;
              setTempValue(momentValue);
            }}
            inputRef={ref as Ref<HTMLInputElement>}
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            sx={{
              mt: 0,
              '.clearButton': {
                marginRight: 0.5,
              },
            }}
            closeOnSelect={false}
            slots={{
              actionBar: () => (
                <ActionBar
                  onNowClick={() => {
                    const now = moment();
                    setInternalValue(now);
                    setTempValue(now);
                    onChange?.(now, { validationError: null });
                    if (onAccept) {
                      onAccept(now);
                    }
                    setOpen(false);
                  }}
                  onClear={() => {
                    setInternalValue(null);
                    setTempValue(null);
                    onChange?.(null, { validationError: null });
                    onClear?.();
                  }}
                  onOk={() => {
                    if (tempValue) {
                      setInternalValue(tempValue);
                      onChange?.(tempValue, { validationError: null });
                      if (onAccept) {
                        onAccept(tempValue);
                      }
                    }
                    setOpen(false);
                  }}
                />
              ),
            }}
            slotProps={{
              field: {
                clearable,
                onClear: () => {
                  setInternalValue(null);
                  setTempValue(null);
                  onChange?.(null, { validationError: null });
                  onClear?.();
                },
              },
              textField: {
                onKeyDown: handleKeyDown,
                onBlur: (
                  event: React.FocusEvent<
                    HTMLInputElement | HTMLTextAreaElement
                  >,
                ) => {
                  if (allowFreeInput && tempValue?.isValid()) {
                    if (isInRange(tempValue)) {
                      commitValue(tempValue);
                    }
                  }
                  onBlur?.(event);
                },
                InputProps: {
                  endAdornment: renderedPickerIcon,
                },
              },
              ...componentCustomStyles,
            }}
          />
          <FormHelperText error>{rangeError || error}</FormHelperText>
        </LocalizationProvider>
      </DateTimePickerRoot>
    );
  },
);

DateTimePicker.displayName = 'Custom Datetime Picker';
